/**
 * Hook per invio messaggi.
 * Stato: `isSending` = testo in partenza (usarlo per bloccare il doppio invio),
 * `isUploading` = allegato in caricamento. Non esiste un campo `sending`.
 */

import { useCallback } from 'react';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import type { MessageType } from '../../types/whatsappChat';

export function useWhatsAppSend() {
  const {
    selectedChatId,
    sendingMessage,
    uploadingMedia,
    replyingTo,
    sendTextMessage,
    sendMediaMessage,
    setReplyingTo,
    retryPendingMessage,
  } = useWhatsAppChatStore();

  // Invia messaggio testo (con supporto menzioni opzionale)
  const sendText = useCallback(
    async (text: string, mentionedJids?: string[]) => {
      if (!selectedChatId || !text.trim()) return;
      await sendTextMessage(text, mentionedJids);
    },
    [selectedChatId, sendTextMessage]
  );

  // Invia media
  const sendMedia = useCallback(
    async (file: File, type?: MessageType, caption?: string) => {
      if (!selectedChatId) return;

      // Auto-detect type from file
      const detectedType = type || detectMediaType(file);
      await sendMediaMessage(file, detectedType, caption);
    },
    [selectedChatId, sendMediaMessage]
  );

  // Invia voice note
  const sendVoice = useCallback(
    async (audio: Blob) => {
      if (!selectedChatId) return;

      // Nome e tipo dal formato vero del vocale (WebM su Chrome, MP4 su Safari, OGG su Firefox):
      // WAHA lo converte da se' (convert: true), il numero Ufficiale lo riconosce dal contenuto.
      const mime = audio.type || 'audio/webm';
      const ext = mime.includes('ogg') ? 'ogg' : mime.includes('mp4') || mime.includes('aac') ? 'm4a' : mime.includes('mpeg') ? 'mp3' : 'webm';
      const file = new File([audio], `voice.${ext}`, { type: mime });
      await sendMediaMessage(file, 'ptt');
    },
    [selectedChatId, sendMediaMessage]
  );

  // Riprova messaggio fallito (prima lo cancellava soltanto)
  const retryMessage = useCallback(
    async (tempId: string) => {
      await retryPendingMessage(tempId);
    },
    [retryPendingMessage]
  );

  return {
    sendText,
    sendMedia,
    sendVoice,
    retryMessage,
    cancelReply: () => setReplyingTo(null),
    replyingTo,
    isSending: sendingMessage,
    isUploading: uploadingMedia,
    canSend: !!selectedChatId && !sendingMessage,
  };
}

function detectMediaType(file: File): MessageType {
  const mime = file.type;

  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';

  return 'document';
}
